import type { FC } from 'react'
import { FileText, UserPlus, Calendar, ClipboardList, FileCheck } from 'lucide-react'
import type { RiskItem, WorkItem, InsightItem, PortCall, CriticalEvent } from '../core/dashboard-types'
import DashboardModal from './DashboardModal'

/* ─────────────────────────────────────────────
   KPI Detail Modal — shows items behind a KPI
   ───────────────────────────────────────────── */

type KpiDetailModalProps = {
  open: boolean
  onClose: () => void
  title: string
  risks: RiskItem[]
  workQueue: WorkItem[]
}

const KpiDetailModal: FC<KpiDetailModalProps> = ({ open, onClose, title, risks, workQueue }) => {
  const showRisks = title.toLowerCase().includes('risk')

  return (
    <DashboardModal open={open} onClose={onClose} title={title} wide>
      {showRisks ? (
        <table className='w-full text-left'>
          <thead>
            <tr className='text-[10px] text-slate-400 uppercase tracking-wider'>
              <th className='pb-2 font-medium'>Severity</th>
              <th className='pb-2 font-medium'>Risk</th>
              <th className='pb-2 font-medium'>Impact</th>
              <th className='pb-2 font-medium'>Vessel</th>
            </tr>
          </thead>
          <tbody className='text-[11px]'>
            {risks.length > 0 ? risks.map((r) => (
              <tr key={r.id} className='border-t border-slate-50'>
                <td className='py-2.5'>
                  <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${
                    r.severity === 'CRITICAL' || r.severity === 'HIGH'
                      ? 'bg-red-50 text-red-700'
                      : r.severity === 'MEDIUM'
                        ? 'bg-amber-50 text-amber-700'
                        : 'bg-emerald-50 text-emerald-700'
                  }`}>
                    {r.severity}
                  </span>
                </td>
                <td className='py-2.5 text-slate-700'>{r.description}</td>
                <td className='py-2.5 text-slate-500'>{r.impact}</td>
                <td className='py-2.5 font-medium text-slate-800'>{r.vessel}</td>
              </tr>
            )) : (
              <tr>
                <td colSpan={4} className='py-10 text-center text-slate-400 text-xs'>
                  No active risks
                </td>
              </tr>
            )}
          </tbody>
        </table>
      ) : (
        <div className='space-y-2'>
          {workQueue.length > 0 ? workQueue.map((item) => {
            const Icon =
              item.type === 'approve' ? FileText :
              item.type === 'assign' ? UserPlus :
              item.type === 'plan' ? Calendar :
              item.type === 'review' ? ClipboardList :
              FileCheck
            return (
              <div key={item.id} className='flex items-start gap-3 p-3 rounded-xl border border-slate-100'>
                <Icon className='w-4 h-4 text-slate-500 mt-0.5' />
                <div className='flex-1'>
                  <p className='text-xs text-slate-700'>{item.description}</p>
                  <p className='text-[11px] text-slate-400'>{item.vessel}</p>
                </div>
                <span className={`text-[10px] font-semibold ${
                  item.priority === 'HIGH' ? 'text-red-600' :
                  item.priority === 'MEDIUM' ? 'text-amber-600' : 'text-emerald-600'
                }`}>
                  {item.priority}
                </span>
              </div>
            )
          }) : (
            <div className='text-center py-10 text-slate-400 text-xs'>No active items</div>
          )}
        </div>
      )}
    </DashboardModal>
  )
}

/* ─────────────────────────────────────────────
   Risk Detail Modal
   ───────────────────────────────────────────── */

type RiskDetailModalProps = {
  open: boolean
  onClose: () => void
  risk: RiskItem | null
}

const RiskDetailModal: FC<RiskDetailModalProps> = ({ open, onClose, risk }) => {
  if (!risk) return null

  return (
    <DashboardModal open={open} onClose={onClose} title='Risk Detail'>
      <div className='space-y-4'>
        <div className='flex items-center gap-3'>
          <span className={`px-3 py-1 rounded text-xs font-semibold ${
            risk.severity === 'CRITICAL' || risk.severity === 'HIGH'
              ? 'bg-red-50 text-red-700'
              : risk.severity === 'MEDIUM'
                ? 'bg-amber-50 text-amber-700'
                : 'bg-emerald-50 text-emerald-700'
          }`}>
            {risk.severity}
          </span>
        </div>
        <div>
          <label className='text-[11px] text-slate-400 uppercase tracking-wider'>Description</label>
          <p className='text-sm text-slate-800 mt-1'>{risk.description}</p>
        </div>
        <div>
          <label className='text-[11px] text-slate-400 uppercase tracking-wider'>Impact</label>
          <p className='text-sm text-slate-800 mt-1'>{risk.impact}</p>
        </div>
        <div>
          <label className='text-[11px] text-slate-400 uppercase tracking-wider'>Vessel</label>
          <p className='text-sm font-semibold text-slate-800 mt-1'>{risk.vessel}</p>
        </div>
      </div>
    </DashboardModal>
  )
}

/* ─────────────────────────────────────────────
   Work Queue Modal — full list with actions
   ───────────────────────────────────────────── */

type WorkQueueModalProps = {
  open: boolean
  onClose: () => void
  items: WorkItem[]
}

const WorkQueueModal: FC<WorkQueueModalProps> = ({ open, onClose, items }) => {
  return (
    <DashboardModal open={open} onClose={onClose} title='Work Queue' wide>
      <div className='space-y-3'>
        {items.map((item) => {
          const Icon =
            item.type === 'approve' ? FileText :
            item.type === 'assign' ? UserPlus :
            item.type === 'plan' ? Calendar :
            item.type === 'review' ? ClipboardList :
            FileCheck

          const priorityStyle =
            item.priority === 'HIGH' ? 'border-l-2 border-l-red-400' :
            item.priority === 'MEDIUM' ? 'border-l-2 border-l-amber-400' :
            'border-l-2 border-l-emerald-400'

          return (
            <div key={item.id} className={`flex items-start gap-3 p-3 rounded-xl border border-slate-100 bg-white ${priorityStyle}`}>
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                item.priority === 'HIGH' ? 'bg-red-50' :
                item.priority === 'MEDIUM' ? 'bg-amber-50' : 'bg-emerald-50'
              }`}>
                <Icon className={`w-4 h-4 ${
                  item.priority === 'HIGH' ? 'text-red-600' :
                  item.priority === 'MEDIUM' ? 'text-amber-600' : 'text-emerald-600'
                }`} />
              </div>
              <div className='flex-1 min-w-0'>
                <p className='text-xs text-slate-700'>{item.description}</p>
                <p className='text-[11px] text-slate-400'>{item.vessel} — {item.dueLabel}</p>
              </div>
              <div className='flex flex-col items-end gap-1.5 flex-shrink-0'>
                <span className={`text-[10px] font-semibold ${
                  item.priority === 'HIGH' ? 'text-red-600' :
                  item.priority === 'MEDIUM' ? 'text-amber-600' : 'text-emerald-600'
                }`}>{item.priority}</span>
                <button className='px-3 py-1 bg-blue-600 text-white text-[11px] font-medium rounded-lg hover:bg-blue-700 transition-colors'>
                  {item.actionLabel}
                </button>
              </div>
            </div>
          )
        })}
      </div>
    </DashboardModal>
  )
}

/* ─────────────────────────────────────────────
   Port Calls Modal — full table
   ───────────────────────────────────────────── */

type PortCallsModalProps = {
  open: boolean
  onClose: () => void
  portCalls: PortCall[]
}

const PortCallsModal: FC<PortCallsModalProps> = ({ open, onClose, portCalls }) => {
  return (
    <DashboardModal open={open} onClose={onClose} title='Upcoming Port Calls' wide>
      <table className='w-full text-left'>
        <thead>
          <tr className='text-[10px] text-slate-400 uppercase tracking-wider'>
            <th className='pb-2 font-medium'>Vessel</th>
            <th className='pb-2 font-medium'>Port</th>
            <th className='pb-2 font-medium'>ETA</th>
            <th className='pb-2 font-medium'>Purpose</th>
          </tr>
        </thead>
        <tbody className='text-[11px]'>
          {portCalls.map((pc) => (
            <tr key={pc.id} className='border-t border-slate-50'>
              <td className='py-2.5 font-medium text-slate-800'>{pc.vessel}</td>
              <td className='py-2.5 text-slate-600'>{pc.port}</td>
              <td className='py-2.5 text-slate-800 font-medium'>{pc.eta}</td>
              <td className='py-2.5 text-slate-500'>{pc.purpose}</td>
            </tr>
          ))}
          {portCalls.length === 0 && (
            <tr><td colSpan={4} className='py-4 text-center text-slate-400 text-xs'>No upcoming port calls</td></tr>
          )}
        </tbody>
      </table>
    </DashboardModal>
  )
}

/* ─────────────────────────────────────────────
   Critical Events Modal — full list
   ───────────────────────────────────────────── */

type CriticalEventsModalProps = {
  open: boolean
  onClose: () => void
  events: CriticalEvent[]
}

const CriticalEventsModal: FC<CriticalEventsModalProps> = ({ open, onClose, events }) => {
  return (
    <DashboardModal open={open} onClose={onClose} title='Upcoming Critical Events'>
      <div className='space-y-3'>
        {events.map((event, idx) => {
          const isHigh = event.severity === 'HIGH'
          const isLast = idx === events.length - 1
          return (
            <div
              key={event.id}
              className={`flex items-start gap-3 ${!isLast ? 'pb-3 border-b border-slate-50' : ''}`}
            >
              <div className='flex-shrink-0 w-12 text-center'>
                <div className={`text-xl font-bold ${isHigh ? 'text-red-600' : 'text-amber-600'} leading-none`}>
                  {event.day}
                </div>
                <div className={`text-[10px] font-medium ${isHigh ? 'text-red-500' : 'text-amber-500'}`}>
                  {event.month}
                </div>
              </div>
              <div className='flex-1 min-w-0'>
                <div className='text-sm font-semibold text-slate-800'>{event.title}</div>
                <div className='text-xs text-slate-500'>{event.vessel}</div>
              </div>
              <div className='text-right flex-shrink-0'>
                <div className='text-xs text-slate-500'>{event.daysLabel}</div>
                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                  isHigh ? 'text-red-600 bg-red-50' : 'text-amber-600 bg-amber-50'
                }`}>
                  {event.severity}
                </span>
              </div>
            </div>
          )
        })}
        {events.length === 0 && (
          <p className='text-center text-slate-400 text-xs py-4'>No critical events</p>
        )}
      </div>
    </DashboardModal>
  )
}

/* ─────────────────────────────────────────────
   Insights Modal — full list
   ───────────────────────────────────────────── */

type InsightsModalProps = {
  open: boolean
  onClose: () => void
  insights: InsightItem[]
}

const InsightsModal: FC<InsightsModalProps> = ({ open, onClose, insights }) => {
  return (
    <DashboardModal open={open} onClose={onClose} title='All Insights'>
      <div className='space-y-3'>
        {insights.map((item) => {
          const colors =
            item.severity === 'amber' ? 'border-amber-200 bg-amber-50/50' :
            item.severity === 'red' ? 'border-red-200 bg-red-50/50' :
            item.severity === 'orange' ? 'border-orange-200 bg-orange-50/50' :
            'border-blue-200 bg-blue-50/50'

          return (
            <div key={item.id} className={`p-3 rounded-xl border ${colors}`}>
              <p className='text-xs text-slate-700'>{item.message}<span className='font-semibold'>{item.highlight}</span></p>
            </div>
          )
        })}
        {insights.length === 0 && (
          <p className='text-center text-slate-400 text-xs py-4'>No insights available</p>
        )}
      </div>
    </DashboardModal>
  )
}

export { KpiDetailModal, RiskDetailModal, WorkQueueModal, PortCallsModal, CriticalEventsModal, InsightsModal, InsightDetailModal }

/* ─────────────────────────────────────────────
   Insight Detail Modal — shows relatable drill-down per insight type
   ───────────────────────────────────────────── */

type InsightDetailModalProps = {
  open: boolean
  onClose: () => void
  insightType: string
  rawDefects: any[]
  rawOverdueJobs: any[]
  rawVoyages: any[]
}

const InsightDetailModal: FC<InsightDetailModalProps> = ({
  open, onClose, insightType, rawDefects, rawOverdueJobs, rawVoyages,
}) => {
  return (
    <DashboardModal open={open} onClose={onClose} title='' wide>
      {insightType === 'i-overdue' && <OverdueJobsDetail jobs={rawOverdueJobs} />}
      {insightType === 'i-old-defects' && <OldDefectsDetail defects={rawDefects} />}
      {insightType === 'i-voyages' && <ActiveVoyagesDetail voyages={rawVoyages} />}
    </DashboardModal>
  )
}

/* ── Sub-renderers ── */

const OverdueJobsDetail: FC<{ jobs: any[] }> = ({ jobs }) => (
  <>
    <h4 className='text-sm font-bold text-slate-900 mb-4'>Overdue PMS Jobs</h4>
    <div className='space-y-3'>
      {jobs.length === 0 && <p className='text-xs text-slate-400'>No overdue PMS jobs.</p>}
      {jobs.map((job: any) => (
        <div key={job.id} className='flex items-start gap-3 p-3 rounded-xl border border-slate-100'>
          <div className='flex-1 min-w-0'>
            <p className='text-xs font-semibold text-slate-800'>{job.title || job.taskDescription || 'Untitled'}</p>
            <div className='flex items-center gap-3 mt-1'>
              <span className='text-[11px] text-slate-500'>{job.vesselName || '—'}</span>
              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                job.criticality === 'CRITICAL' || job.criticality === 'HIGH'
                  ? 'bg-red-50 text-red-700'
                  : job.criticality === 'MEDIUM'
                    ? 'bg-amber-50 text-amber-700'
                    : 'bg-emerald-50 text-emerald-700'
              }`}>{job.criticality || 'MEDIUM'}</span>
            </div>
          </div>
          <div className='text-right flex-shrink-0'>
            <p className='text-[11px] font-medium text-red-600'>{job.dueDate ? formatShortDate(job.dueDate) : '—'}</p>
            <p className='text-[10px] text-slate-400'>Due</p>
          </div>
        </div>
      ))}
    </div>
  </>
)

const OldDefectsDetail: FC<{ defects: any[] }> = ({ defects }) => {
  const oldDefects = defects
    .filter((d: any) => {
      if (d.status === 'CLOSE' || d.status === 'REVIEWED') return false
      if (!d.dateObserved) return false
      const days = (Date.now() - new Date(d.dateObserved).getTime()) / 86400000
      return days > 30
    })
    .sort((a: any, b: any) => new Date(a.dateObserved).getTime() - new Date(b.dateObserved).getTime())

  return (
    <>
      <h4 className='text-sm font-bold text-slate-900 mb-4'>Overdue Audits & Defects</h4>
      <div className='space-y-3'>
        {oldDefects.length === 0 && <p className='text-xs text-slate-400'>No overdue items.</p>}
        {oldDefects.map((d: any) => {
          const daysOpen = Math.round((Date.now() - new Date(d.dateObserved).getTime()) / 86400000)
          return (
            <div key={d.id} className='flex items-start gap-3 p-3 rounded-xl border border-slate-100'>
              <div className='flex-1 min-w-0'>
                <p className='text-xs font-semibold text-slate-800'>{d.displayTitle || d.description || d.defectNumber || 'Untitled'}</p>
                <div className='flex items-center gap-3 mt-1'>
                  <span className='text-[11px] text-slate-500'>{d.vesselName || '—'}</span>
                  <span className='text-[10px] text-slate-400'>{d.dacActionName || ''}</span>
                </div>
              </div>
              <div className='text-right flex-shrink-0'>
                <p className='text-[11px] font-medium text-red-600'>{daysOpen} day{daysOpen > 1 ? 's' : ''}</p>
                <p className='text-[10px] text-slate-400'>Overdue</p>
              </div>
            </div>
          )
        })}
      </div>
    </>
  )
}

const ActiveVoyagesDetail: FC<{ voyages: any[] }> = ({ voyages }) => {
  const activeVoyages = voyages.filter((v: any) => v.active)

  return (
    <>
      <h4 className='text-sm font-bold text-slate-900 mb-4'>Active Voyages</h4>
      <div className='space-y-3'>
        {activeVoyages.length === 0 && <p className='text-xs text-slate-400'>No active voyages.</p>}
        {activeVoyages.map((v: any) => (
          <div key={v.id} className='flex items-start gap-3 p-3 rounded-xl border border-slate-100'>
            <div className='flex-1 min-w-0'>
              <p className='text-xs font-semibold text-slate-800'>{v.vessel?.fleet_name || '—'}</p>
              <div className='flex items-center gap-2 mt-1'>
                <span className='text-[11px] text-slate-500'>{v.departurePort || '—'} → {v.arrivalPort || '—'}</span>
                <span className='text-[10px] font-medium px-2 py-0.5 rounded-full bg-blue-50 text-blue-700'>
                  {v.voyageNumber || ''}
                </span>
              </div>
            </div>
            <div className='text-right flex-shrink-0'>
              <p className='text-[11px] text-slate-600'>{v.startDate ? formatShortDate(v.startDate) : '—'}</p>
              <p className='text-[10px] text-slate-400'>Started</p>
            </div>
          </div>
        ))}
      </div>
    </>
  )
}

function formatShortDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })
  } catch { return iso }
}
